import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { Middleware } from "@reduxjs/toolkit";
import { Sentry } from "@/lib/sentry";

// Type definitions for better TypeScript support
interface ApiAction {
  type: string;
  meta?: {
    arg?: {
      endpointName?: string;
      originalArgs?: unknown;
      type?: string;
    };
    baseQueryMeta?: {
      request?: Request;
      response?: Response;
    };
  };
  payload?: {
    status?: number;
    data?: unknown;
    error?: string;
  };
  error?: {
    status?: number;
    data?: unknown;
    error?: string;
  };
}

// Helper function to determine if this is an API action
const isApiAction = (action: unknown): action is ApiAction => {
  const actionObj = action as { type?: string };
  const actionType = actionObj?.type;
  return actionType !== undefined && (
    actionType.includes("/pending") ||
    actionType.includes("/fulfilled") ||
    actionType.includes("/rejected")
  );
};

// Helper function to extract endpoint name from action
const getEndpointName = (action: ApiAction): string => {
  return action.meta?.arg?.endpointName || "unknown";
};

// Helper function to extract error details
const getErrorDetails = (action: ApiAction) => {
  if (!isRejectedWithValue(action)) return null;

  const payload = action.payload as {
    status?: number;
    error?: string;
    data?: { error?: string };
  };

  return {
    endpointName: getEndpointName(action),
    args: action.meta?.arg?.originalArgs,
    status: payload?.status,
    error: payload?.error || payload?.data?.error,
    data: payload?.data,
  };
};

// Create Sentry Redux middleware
export const sentryReduxMiddleware: Middleware = () => (next) => (action: unknown) => {
  const actionObj = action as { type?: string; payload?: unknown };

  // Add breadcrumb for all actions (excluding noisy ones)
  if (typeof actionObj.type === "string" &&
      !actionObj.type.includes("@@") &&
      !actionObj.type.includes("rtk-query") &&
      !actionObj.type.includes("persist/")) {

    // Only add breadcrumbs for meaningful actions
    Sentry.addBreadcrumb({
      category: "redux",
      message: actionObj.type,
      level: "info",
      data: {
        actionType: actionObj.type,
        // Include minimal payload data (avoid sensitive info)
        hasPayload: !!actionObj.payload,
        payloadType: actionObj.payload ? typeof actionObj.payload : undefined,
      },
    });
  }

  // Handle RTK Query API calls with breadcrumbs only
  if (isApiAction(action)) {
    const endpointName = getEndpointName(action);

    try {
      // Add breadcrumb for API call start
      if (actionObj.type?.includes("/pending")) {
        Sentry.addBreadcrumb({
          category: "api",
          message: `API call started: ${endpointName}`,
          level: "info",
          data: {
            endpointName,
            actionType: actionObj.type,
          },
        });
      }

      // Execute the action
      const result = next(action);

      // Handle successful API calls
      if (actionObj.type?.includes("/fulfilled")) {
        Sentry.addBreadcrumb({
          category: "api",
          message: `API call succeeded: ${endpointName}`,
          level: "info",
          data: {
            endpointName,
            actionType: actionObj.type,
          },
        });
      }

      // Handle failed API calls
      if (actionObj.type?.includes("/rejected")) {
        const errorDetails = getErrorDetails(action);

        // Capture error to Sentry
        if (errorDetails) {
          Sentry.withScope((scope) => {
            scope.setTag("apiCall", true);
            scope.setTag("endpoint", endpointName);
            scope.setTag("http.status_code", errorDetails.status);

            scope.setContext("api_request", {
              endpointName: errorDetails.endpointName,
              status: errorDetails.status,
              error: errorDetails.error,
            });

            // Create error object from the payload
            const error = new Error(errorDetails.error || "API call failed");
            error.name = "APIError";

            Sentry.captureException(error);
          });
        }

        Sentry.addBreadcrumb({
          category: "api",
          message: `API call failed: ${endpointName}`,
          level: "error",
          data: {
            endpointName,
            actionType: actionObj.type,
            status: errorDetails?.status,
            error: errorDetails?.error,
          },
        });
      }

      return result;
    } catch (error) {
      // Handle unexpected errors in the middleware
      Sentry.withScope((scope) => {
        scope.setTag("middleware", "sentryRedux");
        scope.setTag("endpoint", endpointName);
        scope.setContext("middleware_error", {
          actionType: actionObj.type,
          endpointName,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        Sentry.captureException(error);
      });

      throw error;
    }
  }

  // For non-API actions, just proceed normally
  return next(action);
};

// Export a function to manually track Redux state changes
export const trackReduxStateChange = (key: string, value: unknown) => {
  Sentry.addBreadcrumb({
    category: "redux.state",
    message: `State changed: ${key}`,
    level: "info",
    data: {
      key,
      valueType: typeof value,
      // Avoid logging the actual value to prevent sensitive data exposure
      hasValue: value !== undefined && value !== null,
    },
  });
};

// Export a function to track performance-critical Redux operations
export const trackReduxPerformance = (operation: string, duration: number, context?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    category: "redux.performance",
    message: `Redux operation: ${operation}`,
    level: "info",
    data: {
      operation,
      duration: `${duration}ms`,
      ...context,
    },
  });

  // If operation is particularly slow, add a breadcrumb with higher level
  if (duration > 1000) {
    Sentry.addBreadcrumb({
      category: "redux.performance",
      message: `SLOW Redux operation: ${operation} (${duration}ms)`,
      level: "warning",
      data: {
        operation,
        duration: `${duration}ms`,
        ...context,
      },
    });
  }
};

export default sentryReduxMiddleware;